import { readRoster } from '@repo/sdk'
import { LockKeyhole } from 'lucide-react'
import type { ReactNode } from 'react'

import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { expandWolfSiblingChoice, WOLF_SIBLING_PAIR_KEY } from '@/lib/wolf-sibling-configuration'

interface CustomRosterConfigurationProps {
  selectedRoles: ReadonlySet<string>
  villagers: ReadonlySet<number>
  extraRole: string | undefined
  wolfChoice: string | undefined
  wolfSiblingPublicEnabled: boolean
  showModels: boolean
  isRoleAllowed?: (role: string) => boolean
  t: (key: string) => string
  roleName: (key: string) => string
  seatName: (key: string) => string
  onSelect: (key: string, isGood: boolean) => void
  renderModel: (key: string, enabled: boolean) => ReactNode
}

export function CustomRosterConfiguration({
  selectedRoles,
  villagers,
  extraRole,
  wolfChoice,
  wolfSiblingPublicEnabled,
  showModels,
  isRoleAllowed = () => true,
  t,
  roleName,
  seatName,
  onSelect,
  renderModel,
}: CustomRosterConfigurationProps) {
  const { choices } = readRoster({ kind: 'catalog' })
  const gridClass = `grid gap-3 pl-2 ${showModels ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 md:grid-cols-3'}`
  const fixedSeat = (key: string) => (
    <div key={key} className="flex items-center gap-1.5" data-testid={`custom-fixed-${key}`}>
      <Checkbox id={`custom-fixed-${key}`} checked disabled className="border-gray-400 data-[state=checked]:bg-gray-400 data-[state=checked]:text-white" />
      <Label htmlFor={`custom-fixed-${key}`} className="shrink-0 text-sm font-normal !opacity-100">
        {seatName(key)}
      </Label>
      {renderModel(key, true)}
    </div>
  )
  const choice = (key: string, isGood: boolean) => {
    const seats = expandWolfSiblingChoice(key)
    const disabled = seats.some(role => !isRoleAllowed(role))
      || (!isGood && !choices.wolfChoices.includes(key))
    const enabled = !disabled && (isGood ? extraRole === key : wolfChoice === key)
    const id = `custom-${isGood ? 'extra' : 'wolf'}-${key}`
    return (
      <div key={key} className="flex flex-wrap items-center gap-1.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <Checkbox
            id={id}
            checked={enabled}
            disabled={disabled}
            onCheckedChange={() => onSelect(key, isGood)}
            data-testid={id}
            className="disabled:border-dashed disabled:border-muted-foreground"
          />
          <Label htmlFor={id} className={`flex min-w-0 flex-1 items-center gap-1.5 text-sm font-normal ${disabled ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}>
            {seats.map(seat => roleName(seat)).join(' / ')}
            {disabled && <LockKeyhole className="size-3.5 shrink-0" aria-hidden="true" />}
          </Label>
        </div>
        {enabled && seats.map(seat => <span key={seat} className="flex min-w-0">{renderModel(seat, enabled)}</span>)}
      </div>
    )
  }
  const ordinaryWolves = [...selectedRoles].filter(role => /^werewolf(?:_[2-4])?$/.test(role))
  const specialWolfSeats = wolfChoice === WOLF_SIBLING_PAIR_KEY ? 2 : wolfChoice ? 1 : 0
  return (
    <div className="space-y-6" data-testid="custom-roster-configuration">
      <div className="space-y-3">
        <Label className="text-sm font-bold">{t('game.specialRoles')} ({choices.fixed.length + (extraRole ? 1 : 0)})</Label>
        <div className={gridClass}>{choices.fixed.map(seat => fixedSeat(seat))}</div>
        <div
          role="group"
          aria-label={t('game.specialRoles')}
          className={gridClass}
          data-testid="custom-extra-role-group"
        >
          {choices.extraGodChoices.map(key => choice(key, true))}
        </div>
      </div>
      <div className="space-y-3">
        <Label className="text-sm font-medium">{t('game.villagers')} ({villagers.size})</Label>
        <div className={gridClass}>{[...villagers].map(index => fixedSeat(`villager_${index}`))}</div>
      </div>
      <div className="border-t border-gray-200 dark:border-gray-700" />
      <div className="space-y-3">
        <Label className="text-sm font-bold">{t('game.evilFaction')} ({ordinaryWolves.length + specialWolfSeats})</Label>
        <div
          role="group"
          aria-label={t('game.evilFaction')}
          className={gridClass}
          data-testid="custom-wolf-choice-group"
        >
          {choices.wolfCatalog.filter(key => key !== 'mechanical_wolf' && (key !== WOLF_SIBLING_PAIR_KEY || wolfSiblingPublicEnabled))
            .map(key => choice(key, false))}
        </div>
        <div className={gridClass}>{ordinaryWolves.map(seat => fixedSeat(seat))}</div>
      </div>
    </div>
  )
}
